// Estimate tab -> Excel (.xlsx).
//
// Pure functions. The page hands over the rows it ALREADY computed and displays
// (enrichedRollup rows grouped by category / alternate, plus the totals it shows);
// nothing here prices anything, so the file cannot disagree with the screen. Group
// subtotals are the same column sums the page renders above each group's lines.
//
// Written without a spreadsheet dependency (by the same decision as the takeoff
// CSV export): a minimal SpreadsheetML workbook zipped with takeoffArchive's
// store-only zip writer. Numbers are real numeric cells with number formats; text
// that starts with = + - @ is defused so imported descriptions can never run as a
// formula.

import { buildZip } from "./takeoffArchive";
import { romOf } from "./rollupOverrides";

export const AI_DRAFT_LABEL = "AI-assisted draft — estimator review required";
export const INTERNAL_LABEL = "INTERNAL — contains cost, markup and margin; not for client distribution";
export const SNAPSHOT_LABEL = "Snapshot of the Estimate tab. The Estimate tab is the source of truth; make changes there, not in this file.";
export const SHEET_NAME = "Estimate";

// style ids — indexes into the cellXfs list in stylesXml()
export const S = { text: 0, title: 1, head: 2, bandText: 3, bandMoney: 4, money: 5, pct: 6, qty: 7, note: 8, bold: 9, boldMoney: 10, center: 11, romMoney: 12, bandRom: 13, bandBlank: 14, boldPct: 15, mono: 16, label: 17, perSf: 18, srcT: 19, srcM: 20, right: 21 };

export const COLUMNS = [
  { label: "CSI Code", w: 12 }, { label: "Src", w: 6 }, { label: "Line", w: 6 }, { label: "Code", w: 12 }, { label: "Description", w: 46 },
  { label: "Qty", w: 11 }, { label: "Unit", w: 7 }, { label: "Unit Cost", w: 14 }, { label: "Total Direct Cost", w: 16 },
  { label: "Sales Burden %", w: 11 }, { label: "Total Sales Burden", w: 16 }, { label: "Total Cost", w: 15 },
  { label: "Mark Up %", w: 10 }, { label: "Unit Price", w: 14 }, { label: "Total", w: 15 }, { label: "ROM", w: 15 }, { label: "$/SF", w: 11 },
];
const NCOL = COLUMNS.length;
const IDX = { qty: 5, unitCost: 7, direct: 8, burdenPct: 9, burden: 10, cost: 11, markupPct: 12, unitPrice: 13, total: 14, rom: 15, perSf: 16 };

// Excel reads a text cell starting with = + - @ (or a tab / CR) as a formula; prefix an apostrophe.
export const defuse = (s) => (/^[=+\-@\t\r]/.test(s) ? `'${s}` : s);

const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
const cell = (v, s = S.text) => ({ v, s });
const txt = (v, s = S.text) => (v == null || v === "" ? null : cell(defuse(String(v)), s));
const money = (v, s = S.money) => (num(v) == null ? null : cell(num(v), s));
const perSf = (total, sf) => (Number(sf) > 0 && total != null ? total / Number(sf) : null);
const pctCell = (v) => (num(v) == null ? null : cell(num(v) / 100, S.pct)); // page holds 1.5 for 1.5 %; Excel wants 0.015

/** One estimate line, in the page's column order. */
export function lineRow(r, projectSF) {
  const row = new Array(NCOL).fill(null);
  row[0] = txt(r.CSICode, S.mono);
  row[1] = txt(r.Source, r.Source === "M" ? S.srcM : S.srcT);
  row[2] = num(r.Line) == null ? null : cell(num(r.Line), S.text);
  row[3] = txt(r.Code, S.mono);
  row[4] = txt(r.Description);
  row[IDX.qty] = num(r.TotalQty) == null ? null : cell(num(r.TotalQty), S.qty);
  row[6] = txt(r.UOM);
  row[IDX.unitCost] = money(r.unitCost);
  row[IDX.direct] = money(r.DirectCost);
  // "Std." = the line prices through the whole-estimate settings stack, not a per-line rate (as on screen)
  row[IDX.burdenPct] = r.hasPerLineRates ? pctCell(r.salesBurdenPct) : cell("Std.", S.right);
  row[IDX.burden] = r.hasPerLineRates ? money(r.totalSalesBurden) : null;
  row[IDX.cost] = money(r.totalCost);
  row[IDX.markupPct] = r.hasPerLineRates ? pctCell(r.markupPct) : cell("Std.", S.right);
  row[IDX.unitPrice] = money(r.unitPrice);
  row[IDX.total] = money(r.SellValue, S.boldMoney);
  row[IDX.rom] = money(romOf(r.SellValue), S.romMoney);
  row[IDX.perSf] = money(perSf(r.SellValue, projectSF), S.perSf);
  return row;
}

/** The sums the page shows on a category band / alternate header. */
export function groupTotals(rows) {
  return rows.reduce((a, r) => ({
    directCost: a.directCost + (r.DirectCost || 0),
    totalSalesBurden: a.totalSalesBurden + (r.totalSalesBurden || 0),
    totalCost: a.totalCost + (r.totalCost || 0),
    total: a.total + (r.SellValue || 0),
    rom: a.rom + (romOf(r.SellValue) || 0),
  }), { directCost: 0, totalSalesBurden: 0, totalCost: 0, total: 0, rom: 0 });
}

// Summary values sit in column I (Total Direct Cost, 16 wide): column B is the 6-wide Src column and showed ##### there, and the accounting format spreads across the 46-wide Description column.
const summaryRow = (label, valueCell) => [cell(label, S.label), null, null, null, null, null, null, null, valueCell];

function bandRow(label, t, projectSF) {
  const row = new Array(NCOL).fill(null).map(() => cell(null, S.bandBlank));
  row[0] = cell(defuse(label), S.bandText);
  row[IDX.direct] = money(t.directCost, S.bandMoney) || cell(null, S.bandBlank);
  row[IDX.burden] = money(t.totalSalesBurden, S.bandMoney);
  row[IDX.cost] = money(t.totalCost, S.bandMoney);
  row[IDX.total] = money(t.total, S.bandMoney);
  row[IDX.rom] = money(t.rom, S.bandRom);
  row[IDX.perSf] = money(perSf(t.total, projectSF), S.bandMoney);
  for (let i = 0; i < NCOL; i++) if (!row[i]) row[i] = cell(null, S.bandBlank);
  return row;
}

/**
 * @param {object} a
 *   projectName, revisionLabel  — header text
 *   exportedAt (Date)           — header date
 *   revisionId                  — activeRev.RevisionID (header)
 *   machineNote                 — the page's unreviewed-machine-items text ("" / omitted = no line)
 *   projectSF                   — Bid Package ▸ Project SF (null -> $/SF blank, as on screen)
 *   baseGroups                  — [{ label, rows }]  (the page's groupedRollup)
 *   altGroups                   — [{ label, rows }]  (the page's groupedAlternates)
 *   baseTotals                  — { directCost, totalSalesBurden, totalCost } (page's baseTotals)
 *   rollupTotal, rollupRom, gpm, gpmPct — the page's own figures
 * @returns {{ rows: Array<Array<{v,s}|null>>, headerRow: number }}  headerRow is 1-based
 */
export function buildEstimateRows(a) {
  const { projectSF = null, baseGroups = [], altGroups = [], baseTotals = {}, rollupTotal = 0, rollupRom = 0, gpm = 0, gpmPct = null } = a;
  const rows = [];
  const when = a.exportedAt instanceof Date ? a.exportedAt : new Date();
  const stamp = `${when.toISOString().slice(0, 10)} ${when.toISOString().slice(11, 16)} UTC`;
  rows.push([cell(defuse(a.projectName || "Estimate"), S.title)]);
  rows.push([cell("Revision", S.label), cell(defuse(a.revisionLabel || ""), S.bold)]);
  rows.push([cell("Revision ID", S.label), a.revisionId == null ? cell("—", S.right) : cell(Number(a.revisionId), S.bold)]);
  rows.push([cell("Exported", S.label), cell(stamp, S.bold)]);
  rows.push([cell(SNAPSHOT_LABEL, S.note)]);
  rows.push([cell(AI_DRAFT_LABEL, S.note)]);
  rows.push([cell(INTERNAL_LABEL, S.note)]);
  if (a.machineNote) rows.push([cell(defuse(String(a.machineNote)), S.note)]);
  rows.push([]);
  rows.push([cell("Base Scope", S.bold)]);
  rows.push(COLUMNS.map(c => cell(c.label, S.head)));
  const headerRow = rows.length;

  for (const g of baseGroups) {
    rows.push(bandRow(`${g.label} (${g.rows.length})`, groupTotals(g.rows), projectSF));
    for (const r of g.rows) rows.push(lineRow(r, projectSF));
  }
  if (!baseGroups.length) rows.push([cell("No base scope line items yet.", S.note)]);
  else {
    rows.push(bandRow("Scope Total", { directCost: baseTotals.directCost, totalSalesBurden: baseTotals.totalSalesBurden, totalCost: baseTotals.totalCost, total: rollupTotal, rom: rollupRom }, projectSF));
  }

  if (altGroups.length) {
    rows.push([]);
    rows.push([cell("Alternates", S.bold)]);
    rows.push(COLUMNS.map(c => cell(c.label, S.head)));
    for (const g of altGroups) {
      const net = g.rows.reduce((s, r) => s + (r.SellValue || 0), 0);
      const band = bandRow(`${g.label} (${g.rows.length})`, groupTotals(g.rows), projectSF);
      band[IDX.direct] = band[IDX.burden] = band[IDX.cost] = band[IDX.rom] = band[IDX.perSf] = cell(null, S.bandBlank);
      band[IDX.total] = money(net, S.bandMoney);
      band[IDX.unitPrice] = cell(net >= 0 ? "Add" : "Deduct", S.bandText); // the page labels the net "Add: $x" / "Deduct: $x"
      rows.push(band);
      for (const r of g.rows) rows.push(lineRow(r, projectSF));
    }
  }

  if (baseGroups.length) {
    rows.push([]);
    rows.push([cell("Summary (Base Scope only)", S.bold)]);
    rows.push(summaryRow("Project SF", num(projectSF) == null ? cell("—", S.right) : cell(num(projectSF), S.qty)));
    rows.push(summaryRow("Base Scope Total", money(rollupTotal, S.boldMoney)));
    rows.push(summaryRow("Total Cost", money(baseTotals.totalCost, S.boldMoney)));
    rows.push(summaryRow("GPM", money(gpm, S.boldMoney)));
    rows.push(summaryRow("GPM %", num(gpmPct) == null ? cell("—", S.right) : cell(num(gpmPct) / 100, S.boldPct)));
  }
  rows.push([]);
  rows.push([cell(AI_DRAFT_LABEL, S.note)]);
  return { rows, headerRow };
}

// ── SpreadsheetML ───────────────────────────────────────────────────────────

const xmlEsc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, ""); // control characters are not legal in XML 1.0

export function colLetter(i) { // 0 -> A
  let s = "";
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

function sheetXml(rows, headerRow) {
  const out = [];
  rows.forEach((row, ri) => {
    const cells = [];
    row.forEach((c, ci) => {
      if (!c) return;
      const ref = `${colLetter(ci)}${ri + 1}`;
      if (c.v == null) cells.push(`<c r="${ref}" s="${c.s}"/>`);
      else if (typeof c.v === "number") cells.push(`<c r="${ref}" s="${c.s}"><v>${c.v}</v></c>`);
      else cells.push(`<c r="${ref}" s="${c.s}" t="inlineStr"><is><t xml:space="preserve">${xmlEsc(c.v)}</t></is></c>`);
    });
    out.push(cells.length ? `<row r="${ri + 1}">${cells.join("")}</row>` : `<row r="${ri + 1}"/>`);
  });
  const cols = COLUMNS.map((c, i) => `<col min="${i + 1}" max="${i + 1}" width="${c.w}" customWidth="1"/>`).join("");
  const pane = headerRow ? `<sheetViews><sheetView workbookViewId="0"><pane ySplit="${headerRow}" topLeftCell="A${headerRow + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>` : "";
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">${pane}<cols>${cols}</cols><sheetData>${out.join("")}</sheetData><sheetProtection sheet="1" objects="1" scenarios="1" formatColumns="0"/><pageSetup orientation="landscape" fitToHeight="0"/></worksheet>`;
}

// Values matched to the Estimate tab (Estimate.jsx + index.css), not guessed:
//   body font  SHEET_FONT = "Franklin Gothic Book" (the whole .est-sheet); 12px cells -> 9 pt (px x 0.75)
//   header     th: 11px bold, white (#ffffff) on slate #44546a, centered  -> 8.5 pt (8.25 is not an Excel step)
//   band       bandCell: fill #d6dce4, text #1f2937, bold (category bands, alternate headers, Scope Total)
//   mono       "monospace" on CSI Code + Code -> Consolas 9 pt
//   Src        bold, centered; M = --cs-blue #185FA5, T = --text3 #9199A6
//   ROM / $/SF --text2 #5A6170 (ROM bold); Total bold; labels/summary captions --text3 #9199A6
//   rules      td borderBottom rgba(0,0,0,.1) -> hairline #E5E5E5
//   alignment  headings centered; text, Unit and Line left; every number right (Acct: $ pinned left)
//   formats    Acct 2dp (164) / Acct 0dp for ROM (167) / 0.0#% (165) / #,##0.00 qty (166)
//   title 13.5 pt bold = the page's 18px h2; note lines are italic #5A6170 (the page's footer wording, darker for legibility)
const FONT = "Franklin Gothic Book";
function stylesXml() {
  const ACCT = '_("$"* #,##0.00_);_("$"* \\(#,##0.00\\);_("$"* "-"??_);_(@_)';
  const ACCT0 = '_("$"* #,##0_);_("$"* \\(#,##0\\);_("$"* "-"??_);_(@_)';
  const numFmts = [[164, ACCT], [165, "0.0#%"], [166, "#,##0.00"], [167, ACCT0]];
  const font = ({ sz = 9, b, i, color, name = FONT }) => `<font>${b ? "<b/>" : ""}${i ? "<i/>" : ""}<sz val="${sz}"/>${color ? `<color rgb="FF${color}"/>` : ""}<name val="${name}"/></font>`;
  const fonts = [
    font({}),                                   // 0 body
    font({ b: 1 }),                             // 1 bold
    font({ sz: 13.5, b: 1 }),                   // 2 title
    font({ sz: 8.5, b: 1, color: "FFFFFF" }),   // 3 header
    font({ i: 1, color: "5A6170" }),            // 4 note
    font({ name: "Consolas" }),                 // 5 mono
    font({ b: 1, color: "1F2937" }),            // 6 band
    font({ color: "5A6170" }),                  // 7 text2
    font({ b: 1, color: "5A6170" }),            // 8 text2 bold (ROM)
    font({ b: 1, color: "185FA5" }),            // 9 Src M
    font({ b: 1, color: "9199A6" }),            // 10 Src T
    font({ color: "9199A6" }),                  // 11 caption (text3)
  ];
  const fills = ['<fill><patternFill patternType="none"/></fill>', '<fill><patternFill patternType="gray125"/></fill>',
    '<fill><patternFill patternType="solid"><fgColor rgb="FF44546A"/></patternFill></fill>', '<fill><patternFill patternType="solid"><fgColor rgb="FFD6DCE4"/></patternFill></fill>'];
  const borders = ['<border><left/><right/><top/><bottom/><diagonal/></border>',
    '<border><left/><right/><top/><bottom style="thin"><color rgb="FFE5E5E5"/></bottom><diagonal/></border>'];
  // [numFmt, font, fill, border, horizontal]
  const xfs = [
    [0, 0, 0, 1, "left"],       // 0 text
    [0, 2, 0, 0, "left"],       // 1 title
    [0, 3, 2, 1, "center"],     // 2 head
    [0, 6, 3, 1, "left"],       // 3 bandText
    [164, 6, 3, 1, "right"],    // 4 bandMoney
    [164, 0, 0, 1, "right"],    // 5 money
    [165, 0, 0, 1, "right"],    // 6 pct
    [166, 0, 0, 1, "right"],    // 7 qty
    [0, 4, 0, 0, "left"],       // 8 note
    [0, 1, 0, 0, "left"],       // 9 bold
    [164, 1, 0, 1, "right"],    // 10 boldMoney (Total)
    [0, 0, 0, 1, "center"],     // 11 center
    [167, 8, 0, 1, "right"],    // 12 romMoney
    [167, 6, 3, 1, "right"],    // 13 bandRom
    [0, 6, 3, 1, "left"],       // 14 bandBlank
    [165, 1, 0, 1, "right"],    // 15 boldPct
    [0, 5, 0, 1, "left"],       // 16 mono
    [0, 11, 0, 0, "left"],      // 17 label
    [164, 7, 0, 1, "right"],    // 18 perSf
    [0, 10, 0, 1, "center"],    // 19 srcT
    [0, 9, 0, 1, "center"],     // 20 srcM
    [0, 0, 0, 1, "right"],      // 21 right (Std., dashes)
  ];
  const xfXml = xfs.map(([n, f, fi, bd, al]) => `<xf numFmtId="${n}" fontId="${f}" fillId="${fi}" borderId="${bd}" xfId="0"${n ? ' applyNumberFormat="1"' : ""} applyFont="1"${fi ? ' applyFill="1"' : ""}${bd ? ' applyBorder="1"' : ""} applyAlignment="1"><alignment horizontal="${al}" vertical="center"${al === "center" ? ' wrapText="1"' : ""}/></xf>`).join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
    + `<numFmts count="${numFmts.length}">${numFmts.map(([id, c]) => `<numFmt numFmtId="${id}" formatCode="${xmlEsc(c)}"/>`).join("")}</numFmts>`
    + `<fonts count="${fonts.length}">${fonts.join("")}</fonts><fills count="${fills.length}">${fills.join("")}</fills>`
    + `<borders count="${borders.length}">${borders.join("")}</borders>`
    + `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>`
    + `<cellXfs count="${xfs.length}">${xfXml}</cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;
}

const enc = new TextEncoder();

/** rows from buildEstimateRows -> the .xlsx bytes (Uint8Array). */
export function buildEstimateXlsx({ rows, headerRow }, now = new Date()) {
  const files = [
    ["[Content_Types].xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`],
    ["_rels/.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`],
    ["xl/workbook.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${SHEET_NAME}" sheetId="1" r:id="rId1"/></sheets></workbook>`],
    ["xl/_rels/workbook.xml.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`],
    ["xl/styles.xml", stylesXml()],
    ["xl/worksheets/sheet1.xml", sheetXml(rows, headerRow)],
  ];
  return buildZip(files.map(([name, xml]) => ({ name, data: enc.encode(xml) })), now);
}

export function estimateFileName({ project = "", revision = "", date = new Date() } = {}) {
  const base = ["Estimate", project, revision, date.toISOString().slice(0, 10)].filter(Boolean).join("_")
    .replace(/[^A-Za-z0-9._-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  return `${base}.xlsx`;
}
